import { apiClient, type Paginated } from '@/core/api';
import type {
  CandidateStatus,
  InterviewOutcome,
  OfferStatus,
  OpeningStatus,
} from './constants';
import type {
  Candidate,
  CandidateInput,
  Interview,
  InterviewInput,
  JobOpening,
  JobOpeningInput,
  Offer,
  OfferInput,
} from './types';

const OPENINGS = '/recruitment/job-openings/';
const CANDIDATES = '/recruitment/candidates/';
const INTERVIEWS = '/recruitment/interviews/';
const OFFERS = '/recruitment/offers/';

// ---- Openings ----
export interface OpeningListParams {
  page?: number;
  status?: OpeningStatus;
}
export async function listOpenings(params: OpeningListParams = {}): Promise<Paginated<JobOpening>> {
  const { data } = await apiClient.get<Paginated<JobOpening>>(OPENINGS, { params });
  return data;
}
export async function getOpening(id: number | string): Promise<JobOpening> {
  const { data } = await apiClient.get<JobOpening>(`${OPENINGS}${id}/`);
  return data;
}
export async function createOpening(input: JobOpeningInput): Promise<JobOpening> {
  const { data } = await apiClient.post<JobOpening>(OPENINGS, input);
  return data;
}
export async function updateOpening(id: number | string, input: Partial<JobOpeningInput>): Promise<JobOpening> {
  const { data } = await apiClient.patch<JobOpening>(`${OPENINGS}${id}/`, input);
  return data;
}
export async function deleteOpening(id: number | string): Promise<void> {
  await apiClient.delete(`${OPENINGS}${id}/`);
}

// ---- Candidates ----
export interface CandidateListParams {
  page?: number;
  status?: CandidateStatus;
  job_opening?: number;
}
export async function listCandidates(params: CandidateListParams = {}): Promise<Paginated<Candidate>> {
  const { data } = await apiClient.get<Paginated<Candidate>>(CANDIDATES, { params });
  return data;
}
export async function getCandidate(id: number | string): Promise<Candidate> {
  const { data } = await apiClient.get<Candidate>(`${CANDIDATES}${id}/`);
  return data;
}
export async function createCandidate(input: CandidateInput): Promise<Candidate> {
  const { data } = await apiClient.post<Candidate>(CANDIDATES, input);
  return data;
}
export async function updateCandidate(id: number | string, input: Partial<CandidateInput>): Promise<Candidate> {
  const { data } = await apiClient.patch<Candidate>(`${CANDIDATES}${id}/`, input);
  return data;
}
export async function deleteCandidate(id: number | string): Promise<void> {
  await apiClient.delete(`${CANDIDATES}${id}/`);
}

// ---- Interviews ----
export interface InterviewListParams {
  page?: number;
  candidate?: number;
  outcome?: InterviewOutcome;
  /** Exact (case-insensitive) stage_name filter. */
  stage?: string;
}
export async function listInterviews(params: InterviewListParams = {}): Promise<Paginated<Interview>> {
  const { data } = await apiClient.get<Paginated<Interview>>(INTERVIEWS, { params });
  return data;
}
export async function getInterview(id: number | string): Promise<Interview> {
  const { data } = await apiClient.get<Interview>(`${INTERVIEWS}${id}/`);
  return data;
}
export async function createInterview(input: InterviewInput): Promise<Interview> {
  const { data } = await apiClient.post<Interview>(INTERVIEWS, input);
  return data;
}
export async function updateInterview(id: number | string, input: Partial<InterviewInput>): Promise<Interview> {
  const { data } = await apiClient.patch<Interview>(`${INTERVIEWS}${id}/`, input);
  return data;
}
export async function deleteInterview(id: number | string): Promise<void> {
  await apiClient.delete(`${INTERVIEWS}${id}/`);
}

// ---- Offers ----
export interface OfferListParams {
  page?: number;
  status?: OfferStatus;
  candidate?: number;
}
export async function listOffers(params: OfferListParams = {}): Promise<Paginated<Offer>> {
  const { data } = await apiClient.get<Paginated<Offer>>(OFFERS, { params });
  return data;
}
export async function getOffer(id: number | string): Promise<Offer> {
  const { data } = await apiClient.get<Offer>(`${OFFERS}${id}/`);
  return data;
}
export async function createOffer(input: OfferInput): Promise<Offer> {
  const { data } = await apiClient.post<Offer>(OFFERS, input);
  return data;
}
export async function updateOffer(id: number | string, input: Partial<OfferInput>): Promise<Offer> {
  const { data } = await apiClient.patch<Offer>(`${OFFERS}${id}/`, input);
  return data;
}
export async function deleteOffer(id: number | string): Promise<void> {
  await apiClient.delete(`${OFFERS}${id}/`);
}
